export enum PermissionType {
    Read = "r",
    Write = "w",
    Update = "u",
    Delete = "d",
    Manager = "m",
}

export enum PossibleRole {
    Admin = "admin",
    User = "user",
    Developer = "developer",
}

export enum PossiblePackage {
  Brokerage = "brokerage",
    NewsArticle = "news_article",
    AtsRead = "ats_read",
    AtsAction = "ats_action",
    AtsBacktest = "ats_backtest",
    Tickets = "tickets",
    HedgeBeacon = "hedge_beacon",
    HedgeBeaconMarketThoughts = "hedge_beacon_market_thoughts",
    Communications = "communications",
    Developer = "developer",
    TransactionalReports = "transactional_reports",
    ListTraders = "list_traders",
    SignatureDocuments = 'signature_documents',
    Freight = "freight",
    ManagerCustomer = "manage_customers",
    OTC = "otc",
    OTCAdmin = "otc_admin",
    GeneralContract = "general_contract",
    General = "general",
    ClientNotification = "client_notification",
    Employee = "employee",
    Destination = "destination",
    DestinationHours = "destination_hours",
    News = "news",
    ManagePermission= "manage_permissions",
    InvoiceAndStatement = "invoice_and_statements",
    ClientManageUsers = "client_manage_users",
    InventoryMap = "inventory_map",
    CustomerPaymentPdf = "customer_payment_pdf",
    ContractQuantity = "contract_quantity",
    CallLog = "call_log",
    DeferPayment = "defer_payment",
    Chat = "chat",
    CostBasis = "cost_basis",
    KnowledgeTasks = "knowledge_tasks",
    PriceData = "price_data",
    DirectSale = "direct_sale",
    GrainSamples = "grain_samples",
  MinimalCustomer = "minimal_customer",
  RiskManagementCropProfile = "risk_management_crop_profile",
  BirthdayFund = "birthday_fund",
    BeaconFund = "beacon_fund",
  CustomerActivity = "customer_activities",
  GrainPosition = "grain_position",
  ExportSalesTracker = "export_sales_tracker",
  CfrDocument = "cfr_document",
  AchDocuments = "ach_document",
  DestinationPackets = "destination_packets",
  MobilePermissions = "mobile_permissions",
  ContractReport = "contract_report",
  TtOrders = "tt_orders",
  FreightMobile = "freight_mobile",
  GoToChat = "goto_chat",
  WeightTicket = "weight_ticket",
  Assistant = 'assistant',
  PaymentRemittance = 'payment_remittance',
}

export interface StytchMember {
  trusted_metadata: Record<string, unknown>;
}

export interface FeatureDefinition {
    name: string;
    description: string;
    requiredPackages: PossiblePackage[];
    requiredPermissions?: PermissionType[];
    backend_routes?: string[];
    frontend_routes?: string[];
    skipPermissionable?: boolean;
}

export interface FeatureGroup {
    name: string;
    description: string;
    features: FeatureDefinition[];
}

export const FEATURE_GROUPS: FeatureGroup[] = [
    {
        name: "Trading",
        description: "ATS and trading related features",
        features: [
            {
                name: "ATS View",
                description: "View ATS trading information",
                requiredPackages: [PossiblePackage.AtsRead],
            },
            {
                name: "ATS Control",
                description: "Control ATS trading actions",
                requiredPackages: [PossiblePackage.AtsAction],
            },
            {
                name: "ATS Backtest",
                description: "Run ATS backtests",
                requiredPackages: [PossiblePackage.AtsBacktest],
            },
        ]
    },
    {
        name: "OTC",
        description: "OTC",
        features: [
            {
                name: "OTC Basic",
                description: "Basic OTC operations",
                requiredPackages: [PossiblePackage.OTC, PossiblePackage.ListTraders, PossiblePackage.ManagerCustomer],
            },
            {
                name: "OTC Administration",
                description: "Advanced OTC operations",
                requiredPackages: [PossiblePackage.OTC, PossiblePackage.OTCAdmin],
            }
        ]
    },
  {
    name: "Defer Payment",
    description: "",
    features: [
      {
        name: "View All",
        description: "",
        requiredPackages: [PossiblePackage.DeferPayment, PossiblePackage.ManagerCustomer],
      },
      {
        name: "Update Payments",
        description: "",
        requiredPackages: [PossiblePackage.DeferPayment],
        requiredPermissions: [PermissionType.Write, PermissionType.Update, PermissionType.Delete]
      }
    ]
  },
  {
    name: "Knowledge Center",
    description: "Onboarding and Cross-Training Tasks / Knowledge Center",
    features: [
      {
        name: "Users Tasks",
        description: "",
        requiredPackages: [PossiblePackage.KnowledgeTasks],
      },
      {
        name: "See All Tasks",
        description: "That are not related to Special Permissions",
        requiredPackages: [PossiblePackage.KnowledgeTasks],
        requiredPermissions: [PermissionType.Manager],
      }
    ]
  },
  {
    name: "Chat",
    description: "",
    features: [
      {
        name: "Chat with Customers",
        description: "",
        requiredPackages: [PossiblePackage.Chat],
      },
      {
        name: "GoTo Messages Auditing",
        description: "",
        requiredPackages: [PossiblePackage.GoToChat],
      },
    ]
  },
    {
        name: "General Contracts",
        description: "",
        features: [
            {
                name: "General Contracts",
                description: "General & Speech Contracts",
                requiredPackages: [PossiblePackage.GeneralContract, PossiblePackage.General, PossiblePackage.ManagerCustomer, PossiblePackage.ListTraders],
            },
            {
                name: "Manager General Contracts",
                description: "See everyones General Contracts",
                requiredPackages: [PossiblePackage.GeneralContract],
                requiredPermissions: [PermissionType.Manager],
            },
            {
                name: "Inventory Map",
                description: "Upload Inventory Map Files",
                requiredPackages: [PossiblePackage.InventoryMap],
            },
        ]
    },
      {
    name: "Direct Sales",
    description: "",
    features: [
      {
        name: "Direct Sales",
        description: "Direct Sales",
        requiredPackages: [PossiblePackage.DirectSale, PossiblePackage.ManagerCustomer, PossiblePackage.ListTraders, PossiblePackage.General],
      },
      {
        name: "Manager Direct Sales",
        description: "See everyones Direct Sales",
        requiredPackages: [PossiblePackage.DirectSale],
        requiredPermissions: [PermissionType.Manager],
      }
    ]
  },
    {
        name: "Hedge Beacon",
        description: "Hedge Beacon",
        features: [
            {
                name: "Hedge Beacon Analyst Recommendations",
                description: "",
                requiredPackages: [PossiblePackage.HedgeBeacon],
            },
            {
                name: "Hedge Beacon Market Thoughts",
                description: "",
                requiredPackages: [PossiblePackage.HedgeBeaconMarketThoughts],
            }
        ]
    },
    {
        name: "Tickets",
        description: "Tickets",
        features: [
            {
                name: "Tickets",
                description: "Weight and Trading Tickets",
                requiredPackages: [PossiblePackage.Tickets],
            }
        ]
    },
    {
        name: "Mobile App",
        description: "",
        features: [
            {
                name: "Client Notification",
                description: "Client Notification",
                requiredPackages: [PossiblePackage.ClientNotification],
            },
            {
                name: "Staff Contacts",
                description: "",
                requiredPackages: [PossiblePackage.Employee],
            },
            {
                name: "Manage Mobile Users",
                description: "",
                requiredPackages: [PossiblePackage.ClientManageUsers],
            },
            {
                name: "Mobile Permissions",
                description: "",
                requiredPackages: [PossiblePackage.MobilePermissions],
            },
        ]
    },
    {
        name: "Signature Documents",
        description: "",
        features: [
            {
                name: "Signature Documents",
                description: "Signature Documents",
                requiredPackages: [PossiblePackage.SignatureDocuments],
            }
        ]
    },
    {
        name: "Risk Management",
        description: "",
        features: [
            {
                name: "Read Crop Profiles",
                description: "",
                requiredPackages: [PossiblePackage.RiskManagementCropProfile],
                requiredPermissions: [PermissionType.Read],
            },
            {
                name: "Update Crop Profiles",
                description: "",
                requiredPackages: [PossiblePackage.RiskManagementCropProfile],
                requiredPermissions: [PermissionType.Update, PermissionType.Write],
            },
        ]
    },
    {
        name: "Customers",
        description: "",
        features: [
      {
        name: "Minimal Customer Info",
        description: "",
        requiredPackages: [PossiblePackage.MinimalCustomer],
      },
            {
                name: "Manage Customers",
                description: "",
                requiredPackages: [PossiblePackage.ManagerCustomer],
            }, {
                name: "Payment PDF",
                description: "",
                requiredPackages: [PossiblePackage.CustomerPaymentPdf],
            },  {
        name: "Activities",
        description: "",
        requiredPackages: [PossiblePackage.CustomerActivity],
      },
        ]
    },
    {
        name: "Traders",
        description: "",
        features: [
            {
                name: "Manage Traders",
                description: "",
                requiredPackages: [PossiblePackage.ManagerCustomer, PossiblePackage.ListTraders],
            }
        ]
    },
  {
    name: "Price Data",
    description: "",
    features: [
      {
        name: "Futures Contracts",
        description: "",
        requiredPackages: [PossiblePackage.PriceData],
      }
    ]
  },
    {
        name: "Reports",
        description: "",
        features: [
            {
                name: "Reports",
                description: "",
                requiredPackages: [PossiblePackage.TransactionalReports, PossiblePackage.ListTraders, PossiblePackage.ManagerCustomer],
            },
            {
                name: "Contract Quantity Alert",
                description: "",
                requiredPackages: [PossiblePackage.ContractQuantity],
            },
            {
                name: "Contract Reports",
                description: "",
                requiredPackages: [PossiblePackage.ContractReport],
            },
        ]
    },
    {
        name: "Invoice",
        description: "",
        features: [
            {
                name: "Invoice and Statement Report",
                description: "",
                requiredPackages: [PossiblePackage.InvoiceAndStatement, PossiblePackage.ManagerCustomer],
            },
            {
                name: "Payment Remittance",
                description: "",
                requiredPackages: [PossiblePackage.PaymentRemittance],
            },
        ]
    },
    {
        name: "Destination",
        description: "",
        features: [
            {
                name: "Destination",
                description: "",
                requiredPackages: [PossiblePackage.Destination, PossiblePackage.ManagerCustomer],
            }
        ]
    },
  {
    name: "Destination Hours",
    description: "",
    features: [
      {
        name: "Destination Hours",
        description: "",
        requiredPackages: [PossiblePackage.DestinationHours],
      }
    ]
  },
    {
        name: "Common",
        description: "",
        features: [
            {
                name: "Locations",
                description: "",
                requiredPackages: [PossiblePackage.General],
            },
            {
                name: "Cost Basis",
                description: "",
                requiredPackages: [PossiblePackage.CostBasis],
            },
            {
                name: "Market Zones",
                description: "",
                requiredPackages: [PossiblePackage.General],
            },
            {
                name: "Staff Contacts",
                description: "",
                requiredPackages: [PossiblePackage.Employee],
            }
        ]
    },
    {
        name: "News Articles",
        description: "",
        features: [
            {
                name: "News",
                description: "",
                requiredPackages: [PossiblePackage.News],
            }
        ]
    },
    {
        name: "Call Logs",
        description: "",
        features: [
            {
                name: "Read your Call Logs",
                description: "",
                requiredPackages: [PossiblePackage.CallLog],
                requiredPermissions: [PermissionType.Read],
            },
            {
                name: "See everyone's Call Logs",
                description: "",
                requiredPackages: [PossiblePackage.CallLog],
                requiredPermissions: [PermissionType.Manager],
            },
        ]
    },
    {
        name: "Grain Samples",
        description: "",
        features: [
            {
                name: "Grain Samples",
                description: "",
                requiredPackages: [PossiblePackage.GrainSamples],
            },
        ]
    },
  {
    name: "Fund",
    description: "",
    features: [
      {
        name: "Birthday Fund",
        description: "",
        requiredPackages: [PossiblePackage.BirthdayFund],
      },
      {
        name: "Beacon Fund",
        description: "",
        requiredPackages: [PossiblePackage.BeaconFund],
      }
    ]
  },
  {
    name: "Brokerage",
    description: "",
    features: [
      {
        name: "Statements",
        description: "",
        requiredPackages: [PossiblePackage.Brokerage],
      },
      {
        name: "TT Orders",
        description: "",
        requiredPackages: [PossiblePackage.TtOrders],
      }
    ]
  },
  {
    name: "Grain Positions",
    description: "",
    features: [
      {
        name: "Preview",
        description: "",
        requiredPackages: [PossiblePackage.GrainPosition],
      }
    ]
  },
  {
    name: "Export Sales Tracker",
    description: "",
    features: [
      {
        name: "Manage Sales Details",
        description: "",
        requiredPackages: [PossiblePackage.ExportSalesTracker],
      }
    ]
  },
  {
    name: "Signing Contracts",
    description: "",
    features: [
      {
        name: "CFR Document",
        description: "",
        requiredPackages: [PossiblePackage.CfrDocument],
      },
      {
        name: "ACH Document",
        description: "",
        requiredPackages: [PossiblePackage.AchDocuments],
        skipPermissionable: true,
      },
      {
        name: "Destination Packets",
        description: "",
        requiredPackages: [PossiblePackage.DestinationHours],
      }
    ]
  },
  {
    name: "Freight",
    description: "",
    features: [
      {
        name: "Freight Images",
        description: "",
        requiredPackages: [PossiblePackage.FreightMobile],
      }
    ]
  },
  {
    name: "Agtech",
    description: "",
    features: [
      {
        name: "Tickets",
        description: "",
        requiredPackages: [PossiblePackage.WeightTicket],
      }
    ]
  },
  {
    name: "AI",
    description: "",
    features: [
      {
        name: "Assistant",
        description: "",
        requiredPackages: [PossiblePackage.Assistant],
      }
    ]
  },
    // {
    //     name: "Developer",
    //     description: "",
    //     features: [
    //         {
    //             name: "Developer",
    //             description: "",
    //             requiredPackages: [PossiblePackage.Developer],
    //         }
    //     ]
    // }
];

function isExemptedFromAdminPackage({ requiredPackage }: { requiredPackage: PossiblePackage }) {
    return [PossiblePackage.AchDocuments, PossiblePackage.Developer].includes(requiredPackage);
}

function isExemptedFromAdminPackages({ requiredPackages }: { requiredPackages: PossiblePackage[] }) {
    return requiredPackages.some((requiredPackage: PossiblePackage) => isExemptedFromAdminPackage({ requiredPackage }));
}

// NOTE: Currently this is expected to be used in route handles after package auth has already been established.
// To check if the user has only specific permissions
// if no colon permissions its assumed you have user permissions for that package.
// If colon, then it assumes you only have those permissions. so hi:r is only Read for that package
// The manager option dictates managing your own content and viewing others (sometimes with audit info)
// so if you have the base package no colon, but its requesting Manager permission and you are not an admin or developer
// it assumes you dont have the right.
export function doesUserPackageHavePermissionTypes({ role, packages, requiredPackage, requiredPermissions }: { role: string; packages: string[]; requiredPermissions: PermissionType[]; requiredPackage: PossiblePackage }) {
    if (role === PossibleRole.Admin && !isExemptedFromAdminPackage({ requiredPackage })) return true;
    if (role === PossibleRole.Developer) return true;

    if (!requiredPermissions.length) return true;

    const splitPackages = packages.map((aPackage: string) => aPackage.split(":")[0]!);
    const foundPackageIndexes = splitPackages.map((aPackage: string, index: number) => { if (aPackage === requiredPackage) { return index; } else { return undefined; }}).filter((item: number | undefined): item is number => item != undefined);

    if (!foundPackageIndexes.length) return false;
    const foundPackage = packages.find((aPackage: string, index: number) => foundPackageIndexes.includes(index) && aPackage.includes(":")) || packages[foundPackageIndexes[0]!];

    const hasPermissionList = foundPackage!.includes(":");
    if (requiredPermissions.includes(PermissionType.Manager) && !hasPermissionList) return false;

    if (!hasPermissionList) return true;
    const permissionSection = foundPackage!.split(":")[1];

    for (const permission of requiredPermissions) {
        if (!permissionSection!.includes(permission)) {
            return false;
        }
    }

    return true;
}

export function doesUserHavePackages({ requiredPackages, member }: { requiredPackages?: PossiblePackage[], member?: StytchMember | null }) {
    if (!requiredPackages) return true;
    if (!requiredPackages.length) return true;
    if (!member) return false;

    const basePackages = getUserPackages(member);
    const role = getUserRole(member);
    const packages = basePackages.map(aPackage => aPackage.split(":")[0]);

    // NOTE: Admin roles does not superseed developer requirement
    // Dont let Admins do actions that they dont understand the full context of.
    if (!!requiredPackages.includes(PossiblePackage.Developer) && !packages.includes(PossiblePackage.Developer)) {
        return false;
    }

    if (role === PossibleRole.Admin && !isExemptedFromAdminPackages({ requiredPackages })) return true;

    if (!packages.length) return false;

    for (const requiredPackage of requiredPackages) {
        if (!packages.includes(requiredPackage)) return false;
    }

    return true;
}

export function getUserPackages(member: StytchMember): PossiblePackage[] {
    return (member?.trusted_metadata?.["packages"] as PossiblePackage[] | undefined) || [];
}

export function getUserRole(member: StytchMember): PossibleRole {
    return (member?.trusted_metadata?.["role"] as PossibleRole | undefined) || PossibleRole.User;
}

export function getFeaturesByPackages(packages: PossiblePackage[]): FeatureDefinition[] {
    return FEATURE_GROUPS.flatMap(group =>
        group.features.filter(feature =>
            feature.requiredPackages.every(pkg => packages.includes(pkg))
        )
    );
}

export function getRequiredPackagesForFeature(featureName: string): PossiblePackage[] {
    const feature = FEATURE_GROUPS.flatMap(g => g.features)
        .find(f => f.name === featureName);
    return feature?.requiredPackages || [];
}
